import {Button} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import cx from 'classnames';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {t} from 'i18next';
import {ReactNode, useState} from 'react';
import {LinkProps} from 'react-router';

import styles from './Notifications.module.css';
import {isThreadNotificationLink} from './utils/threadLink';
import {useNotificationArchiver} from './utils/useNotificationArchiver';
import {useNotificationContext} from './utils/useNotificationContext';

const NotificationIcon = ({
  accessory,
  upcoming,
  last,
}: {
  accessory: ReactNode;
  upcoming?: boolean;
  last?: boolean;
}) => {
  if (!upcoming) {
    return <>{accessory}</>;
  }
  return (
    <Box position="relative">
      <Box
        position="absolute"
        backgroundColor="Border Subtle"
        style={{width: 1, top: -12, height: 12, left: 16.5}}
      />
      <Box
        position="absolute"
        backgroundColor="Border Subtle"
        style={{width: 1, top: 34, height: last ? 14 : 22, left: 16.5}}
      />
      <Box
        borderRadius="Circular"
        borderWidth="1"
        borderStyle="Solid"
        borderColor="Border Subtle"
        padding="4"
        style={{height: 34}}
      >
        {accessory}
      </Box>
    </Box>
  );
};

export const NotificationCard = ({
  accessory,
  header,
  subtext,
  buttonLink,
  archived,
  truncate,
  variant = 'standard',
  to,
  clickAction,
}: {
  accessory: ReactNode;
  header: ReactNode;
  subtext: ReactNode;
  archived: boolean;
  truncate?: boolean;
  variant?: 'alert' | 'standard'; // | 'attention' | 'success' | 'warning';
  to?: string;
  buttonLink?: {cta: string; to: string; state?: LinkProps['state']};
  clickAction?: (click: 'view' | 'archive') => void;
}) => {
  const {
    separator,
    onClick,
    archiveable,
    upcoming,
    notification: {id, type},
  } = useNotificationContext();
  const {archiveNotification} = useNotificationArchiver();
  const [isArchiving, setIsArchiving] = useState(false);
  const selectable = Boolean((to && !buttonLink) || (archived && archiveable)) && !upcoming;
  const markNotificationRead = (click: 'view' | 'archive') => {
    clickAction?.(click);
    if (archived) {
      analyticsLogger().logEvent('NOTIFICATION_ARCHIVE_CLICKED', {type});
      analyticsLogger().logEvent('NOTIFICATION_CLICKED', {
        channel: 'notification_center',
        notification_id: id,
        type,
        click: 'view',
      });
      onClick?.();
      return;
    }
    setIsArchiving(true);
    archiveNotification({id}).finally(() => {
      setIsArchiving(false);
    });
    analyticsLogger().logEvent('NOTIFICATION_INBOX_CLICKED', {type, click});
    if (click !== 'archive') {
      analyticsLogger().logEvent('NOTIFICATION_CLICKED', {
        channel: 'notification_center',
        notification_id: id,
        type,
        click,
      });
      onClick?.();
    }
  };

  const handleCTA = () => {
    // Some action items can archive on CTA click
    if (['gratitude_received'].includes(type)) {
      archiveNotification({id});
      analyticsLogger().logEvent('NOTIFICATION_INBOX_CLICKED', {type, click: 'cta'});
    }

    analyticsLogger().logEvent('NOTIFICATION_CTA_CLICKED', {type});
    analyticsLogger().logEvent('NOTIFICATION_CLICKED', {
      channel: 'notification_center',
      notification_id: id,
      type,
      click: 'cta',
    });
    onClick?.();
  };

  return (
    <Link
      isClickable={selectable}
      to={to || ''}
      hasNoUnderline
      onClick={() => markNotificationRead('view')}
      state={{source: 'notification'}}
      preventScrollReset={isThreadNotificationLink(to)}
    >
      <List.Item
        gap={upcoming ? 'small' : 'medium'}
        isSelectable={selectable}
        className={cx(
          styles.notification,
          atoms({
            paddingY: '12',
            paddingX: '16',
            borderBottom: upcoming ? undefined : 'Solid',
            borderWidth: separator && variant !== 'alert' ? '1' : '0',
            borderColor: 'Border Subtle',
            backgroundColor: variant === 'alert' ? 'Alert Surface' : 'Background Raised',
          })
        )}
      >
        <List.Accessory style={{alignSelf: 'flex-start'}}>
          <NotificationIcon accessory={accessory} upcoming={upcoming} last={separator} />
        </List.Accessory>
        <List.Content>
          <LabelGroup
            withText={
              !truncate ? (
                header
              ) : (
                <Box
                  style={{
                    WebkitLineClamp: 2,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    display: '-webkit-box',
                  }}
                >
                  {header}
                </Box>
              )
            }
            withSubtext={
              <Split alignY="center" gap="8">
                <Split.Item>{subtext}</Split.Item>
                <Split.Item>{archiveable && !archived ? '•' : null}</Split.Item>
                <Split.Item>
                  {archiveable && !archived ? (
                    <Button
                      variant="transparent"
                      size="small"
                      onClick={(e: any) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isArchiving) {
                          return;
                        }
                        markNotificationRead('archive');
                      }}
                    >
                      <Text size="small" color="faint">
                        {t('archive')}
                      </Text>
                    </Button>
                  ) : null}
                </Split.Item>
              </Split>
            }
          />
        </List.Content>
        {!archived ? (
          buttonLink ? (
            <List.Accessory>
              <ButtonLink
                to={buttonLink.to}
                state={{...buttonLink.state, source: 'notification'}}
                size="small"
                variant="outline"
                onClick={handleCTA}
              >
                {buttonLink.cta}
              </ButtonLink>
            </List.Accessory>
          ) : (
            <List.Accessory style={{marginLeft: 0}}>
              <UIIcon
                visibility="hidden"
                src={ChevronRightLine}
                className={upcoming ? undefined : styles.showChevron}
              />
            </List.Accessory>
          )
        ) : (
          buttonLink && (
            <List.Accessory style={{marginLeft: 0}}>
              <UIIcon src={CheckmarkLine} className={atoms({color: 'Text Subtle'})} />
            </List.Accessory>
          )
        )}
      </List.Item>
    </Link>
  );
};
